import { useState } from "react";
import type { FaqEntry } from "@photography-booking/shared";
import { Section } from "../components/Section.js";
import { DEFAULT_FAQS } from "../content/site-content.js";

interface FaqProps {
  /** FAQ entries from the API; defaults to placeholder content. */
  entries?: readonly FaqEntry[];
}

/**
 * FAQ section (Req 1.2, 12.1-12.3). Entries are rendered in order as a list of
 * collapsible disclosure items: every entry starts collapsed, selecting a
 * collapsed entry expands it to reveal its answer (Req 12.2), and selecting an
 * expanded entry collapses it to hide its answer again (Req 12.3). The
 * expand/collapse state is keyed by `faqId`, so the toggle is driven entirely
 * by the `entries` prop.
 */
export function Faq({ entries = DEFAULT_FAQS }: FaqProps): JSX.Element {
  const ordered = [...entries].sort((a, b) => a.order - b.order);
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const toggle = (faqId: string): void => {
    setExpandedIds((current) => {
      const next = new Set(current);
      // Selecting an expanded entry collapses it; a collapsed entry expands.
      if (next.has(faqId)) {
        next.delete(faqId);
      } else {
        next.add(faqId);
      }
      return next;
    });
  };

  return (
    <Section id="faq" title="FAQs" subtitle="Answers to common questions">
      <ul className="faq__list">
        {ordered.map((entry) => {
          const isExpanded = expandedIds.has(entry.faqId);
          const answerId = `faq-answer-${entry.faqId}`;
          return (
            <li key={entry.faqId} className="faq__item">
              <h3 className="faq__question">
                <button
                  type="button"
                  className="faq__trigger"
                  aria-expanded={isExpanded}
                  aria-controls={answerId}
                  onClick={() => toggle(entry.faqId)}
                >
                  <span className="faq__question-text">{entry.question}</span>
                  <span className="faq__icon" aria-hidden="true">
                    {isExpanded ? "\u2212" : "+"}
                  </span>
                </button>
              </h3>
              {isExpanded ? (
                <p id={answerId} className="faq__answer">
                  {entry.answer}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>
    </Section>
  );
}
